import { useRef } from 'react';
import {
  UserConfig,
  SaveUserConfigPayload,
  SERVER_SYNCED_FLAG_KEYS,
  IFlagsConfig,
} from '@/models';
import { apiUser } from '@/utils/api/apiUser';
import { logger } from '@/utils';
import { useDispatch, useSelector } from 'react-redux';
import {
  PopupActionType,
  setCustomizedBlockPage,
  setGroups,
  setPasswordProtection,
  showPopupMessage,
  updateFlagsConfig,
} from '@/store/reducers';
import { LOGGER_FEATURE, LOGGER_ACTION } from '@/constants/logger/logger-tags';
import { IMAGES } from '@/constants/ui/images';
import { useExtension } from '@/hooks/integrations/useExtension.hook';
import {
  selectFlagsConfig,
  selectGroups,
  selectPasswordProtection,
  selectCustomizedBlockPage,
} from '@/store/selectors';
import {
  getLocalConfigChangesFromDefault,
  getConsentSnapshot,
  getClearedConsentFlags,
  mergeConsentFlags,
} from '@/utils/helpers/user-config';
import {
  clearCachedBlockPageBackground,
  readCachedBlockPageBackground,
} from '@/utils/helpers/customize-block-page';
import { pick } from 'lodash';

export const useSync = () => {
  const dispatch = useDispatch();
  const extensionHook = useExtension();
  const flagsConfig = useSelector(selectFlagsConfig);
  const groups = useSelector(selectGroups);
  const passwordProtection = useSelector(selectPasswordProtection);
  const customizedBlockPage = useSelector(selectCustomizedBlockPage);

  // Keep refs updated with latest values to avoid stale closures in useCallback
  const flagsConfigRef = useRef<IFlagsConfig>(flagsConfig);
  flagsConfigRef.current = flagsConfig;

  /**
   * store user config into the store
   * @param userConfig
   */
  const updateUserConfigInStore = (userConfig: UserConfig) => {
    if (userConfig?.customizedBlockPage) {
      if (userConfig.customizedBlockPage.backgroundURL) {
        const cachedBackground = readCachedBlockPageBackground();
        if (cachedBackground) {
          dispatch(
            setCustomizedBlockPage({ backgroundImageInByte: cachedBackground })
          );
        }
      } else {
        clearCachedBlockPageBackground();
      }
      dispatch(setCustomizedBlockPage(userConfig.customizedBlockPage));
    }
    if (userConfig?.passwordProtection) {
      dispatch(setPasswordProtection(userConfig.passwordProtection));
    }
    if (userConfig?.groups) {
      dispatch(setGroups(userConfig.groups));
    }
    if (userConfig?.flagsConfig) {
      dispatch(updateFlagsConfig(userConfig.flagsConfig));
    }
  };

  // Helper functions
  const createLogTags = (action: string) => ({
    tags: {
      feature: LOGGER_FEATURE.USER,
      action,
    },
  });

  const showGeneralError = () => {
    dispatch(
      showPopupMessage({
        iconSrc: IMAGES.EXCLAMATION,
        titleKey: 'generalError.title',
        subtitleKey: 'generalError.subtitle',
        buttonTextKey: 'generalError.buttonText',
        buttonAction: { type: PopupActionType.RELOAD },
      })
    );
  };

  /**
   * Saves initial user config to server, always preserving current flagsConfig.
   * @param localChanges - Optional config changes from anonymous session (already processed)
   */
  const initializeDefaultUserConfig = async (
    localChanges?: SaveUserConfigPayload
  ): Promise<void> => {
    const logTags = createLogTags(LOGGER_ACTION.SAVE_USER_CONFIG);
    try {
      const currentFlagsConfig = flagsConfigRef.current;
      const configToSave: SaveUserConfigPayload = localChanges
        ? localChanges
        : { flagsConfig: pick(currentFlagsConfig, SERVER_SYNCED_FLAG_KEYS) };

      const savedConfig = await apiUser.saveUserConfig(configToSave);
      updateUserConfigInStore(savedConfig);
      await extensionHook.notifyExtensionConfigChanged();
      logger.info('Default user config initialized via server', logTags);
    } catch (error) {
      logger.error('Failed to initialize default user config', error, logTags);
      showGeneralError();
    }
  };

  /**
   * GET the account config on login and union the anonymous consent onto it, so
   * neither the anonymous session nor the account loses buckets. Persists the
   * union so a later GET cannot drop it, then returns the config to store.
   */
  const fetchConfigWithCarriedConsent = async (
    logTags: ReturnType<typeof createLogTags>
  ): Promise<UserConfig> => {
    const consentSnapshot = getConsentSnapshot(flagsConfigRef.current);
    const userConfig = await apiUser.getUserConfig();
    if (Object.keys(consentSnapshot).length === 0) {
      return userConfig;
    }
    const mergedConsent = mergeConsentFlags(
      consentSnapshot,
      userConfig?.flagsConfig
    );
    try {
      await apiUser.updateFlagConfig(mergedConsent);
    } catch (patchError) {
      logger.error('Failed to persist carried consent', patchError, logTags);
    }
    return {
      ...userConfig,
      flagsConfig: { ...userConfig?.flagsConfig, ...mergedConsent },
    };
  };

  /**
   * Fetch user config from remote. Runs on every app init, so the anonymous consent
   * is only carried when mergeAnonymousConsent is set (the login transition). On 404
   * (new user) the consent flags are already part of the saved local changes.
   */
  const syncFromRemote = async (
    mergeAnonymousConsent = false
  ): Promise<void> => {
    const logTags = createLogTags(LOGGER_ACTION.SYNC_FROM_REMOTE);

    try {
      const userConfig = mergeAnonymousConsent
        ? await fetchConfigWithCarriedConsent(logTags)
        : await apiUser.getUserConfig();
      updateUserConfigInStore(userConfig);
      logger.info('User config synced successfully from remote', logTags);
    } catch (error: unknown) {
      const errorWithStatus = error as { status?: number };
      if (errorWithStatus?.status === 404) {
        logger.info(
          'User config not found (404), initializing with defaults',
          logTags
        );

        const localChanges = getLocalConfigChangesFromDefault({
          groups,
          passwordProtection,
          customizedBlockPage,
          flagsConfig: flagsConfigRef.current,
        });

        await initializeDefaultUserConfig(localChanges);
        return;
      }

      logger.error('Failed to load user config', error, logTags);
      showGeneralError();
    }
  };

  /**
   * Logout: clear consent for the new anonymous user. Resets consent flags in Redux
   * and saves the cleared config to the new UID; initializeDefaultUserConfig notifies
   * the extension so it refetches the cleared config. Non-consent flags are kept.
   */
  const clearConsentAndInitialize = async (): Promise<void> => {
    const clearedConsent = getClearedConsentFlags(flagsConfigRef.current);
    dispatch(updateFlagsConfig(clearedConsent));
    flagsConfigRef.current = { ...flagsConfigRef.current, ...clearedConsent };
    await initializeDefaultUserConfig();
  };

  return {
    syncFromRemote,
    initializeDefaultUserConfig,
    clearConsentAndInitialize,
  };
};
